import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, downloadBlob } from "@/lib/api";
import { PageHeader, Loading } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Lock, Download, Play, RotateCcw, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { formatINR, MONTH_OPTIONS, monthName } from "@/utils/format";

const now = new Date();

export default function Payroll() {
  const [month, setMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [year, setYear] = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setData(null);
    api.get(`/payroll/preview?year=${year}&month=${month}`)
      .then(({ data }) => { setData(data); setEdits({}); })
      .catch((e) => { toast.error(formatApiError(e)); setData({ rows: [], locked: false }); });
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const getEdit = (oid, field, fallback) => (edits[oid]?.[field] ?? fallback);
  const setEdit = (oid, field, value) => setEdits((p) => ({ ...p, [oid]: { ...p[oid], [field]: value } }));

  const generate = async () => {
    setBusy(true);
    try {
      const rows = data.rows.map((r) => ({
        employee_oid: r.employee_oid,
        lop_days: Number(getEdit(r.employee_oid, "lop_days", 0)) || 0,
        tds: Number(getEdit(r.employee_oid, "tds", 0)) || 0,
        other_label: getEdit(r.employee_oid, "other_label", "") || "",
        other_amount: Number(getEdit(r.employee_oid, "other_amount", 0)) || 0,
      }));
      const { data: res } = await api.post("/payroll/generate", { year, month, rows });
      toast.success(`Generated ${res.count} payslips`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    try { const { data: res } = await api.post("/payroll/revoke", { year, month }); toast.success(`Revoked ${res.count} payslips`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const downloadZip = () => downloadBlob(`/payroll/${year}/${month}/zip`, `Payslips_${monthName(month)}_${year}.zip`)
    .catch((e) => toast.error(formatApiError(e)));
  const downloadOne = (r) => downloadBlob(`/payroll/payslips/${r.id}/download`, `Payslip_${r.employee_id}_${monthName(month)}_${year}.pdf`)
    .catch((e) => toast.error(formatApiError(e)));

  return (
    <div>
      <PageHeader title="Payroll Runs" description="Review the month, adjust LOP / TDS / deductions, then generate. Generated months are locked until revoked." testid="payroll-page">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-36" data-testid="payroll-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{MONTH_OPTIONS.map((m) => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="payroll-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[year - 1, year, now.getFullYear(), now.getFullYear() - 1].filter((v, i, a) => a.indexOf(v) === i).sort().map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      {data === null ? <Loading /> : (
        <>
          {data.locked && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-foreground text-background rounded-md px-5 py-4 mb-6" data-testid="locked-banner">
              <div className="flex items-center gap-2.5">
                <Lock className="w-4 h-4" />
                <span className="text-sm">This month is <b>generated & locked</b>. Payslips are immutable.</span>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" className="gap-1.5" onClick={downloadZip} data-testid="download-zip-button"><Download className="w-4 h-4" /> Download all (ZIP)</Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="secondary" className="gap-1.5 text-destructive" data-testid="revoke-button"><RotateCcw className="w-4 h-4" /> Revoke</Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Revoke {monthName(month)} {year}?</AlertDialogTitle>
                      <AlertDialogDescription>This voids all payslips for this month and writes an audit entry. You can then regenerate.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={revoke} data-testid="confirm-revoke">Revoke & unlock</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}

          {data.rows.length === 0 ? (
            <div className="border border-dashed rounded-md py-16 text-center text-sm text-muted-foreground" data-testid="payroll-empty">
              No active employees to run payroll for.
            </div>
          ) : (
            <div className="bg-card border border-border rounded-md overflow-x-auto" data-testid="payroll-table">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="label-caps border-b border-border bg-secondary/50">
                    <th className="text-left px-4 py-3">Employee</th>
                    <th className="text-center px-2 py-3">Work / Paid</th>
                    <th className="text-center px-2 py-3 w-20">LOP</th>
                    <th className="text-right px-2 py-3">Gross</th>
                    <th className="text-right px-2 py-3">PT</th>
                    <th className="text-center px-2 py-3 w-28">TDS (₹)</th>
                    <th className="text-center px-2 py-3 w-40">Other deduction</th>
                    <th className="text-right px-4 py-3">Net</th>
                    {data.locked && <th className="px-2 py-3"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {data.rows.map((r) => (
                    <tr key={r.employee_oid} data-testid={`payroll-row-${r.employee_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.full_name}</div>
                        <div className="text-xs text-muted-foreground tabular">{r.employee_id} · {r.designation}</div>
                      </td>
                      <td className="text-center px-2 py-3 tabular text-muted-foreground">{r.working_days} / {r.days_paid}{r.prorated && <span className="text-[#FF4500]"> *</span>}</td>
                      <td className="px-2 py-3">
                        {data.locked ? <div className="text-center tabular">{r.lop_days}</div> :
                          <Input type="number" min={0} className="h-8 text-center" value={getEdit(r.employee_oid, "lop_days", 0)}
                            onChange={(e) => setEdit(r.employee_oid, "lop_days", e.target.value)} data-testid={`lop-${r.employee_id}`} />}
                      </td>
                      <td className="text-right px-2 py-3 tabular">{formatINR(r.gross)}</td>
                      <td className="text-right px-2 py-3 tabular text-muted-foreground">{formatINR(r.pt)}</td>
                      <td className="px-2 py-3">
                        {data.locked ? <div className="text-center tabular">{formatINR(r.tds)}</div> :
                          <Input type="number" min={0} className="h-8 text-center" value={getEdit(r.employee_oid, "tds", 0)}
                            onChange={(e) => setEdit(r.employee_oid, "tds", e.target.value)} data-testid={`tds-${r.employee_id}`} />}
                      </td>
                      <td className="px-2 py-3">
                        {data.locked ? <div className="text-center tabular text-muted-foreground">{r.other ? formatINR(r.other) : "—"}</div> :
                          <div className="flex gap-1">
                            <Input placeholder="Label" className="h-8 w-20" value={getEdit(r.employee_oid, "other_label", "")}
                              onChange={(e) => setEdit(r.employee_oid, "other_label", e.target.value)} />
                            <Input type="number" min={0} placeholder="₹" className="h-8 w-16 text-center" value={getEdit(r.employee_oid, "other_amount", "")}
                              onChange={(e) => setEdit(r.employee_oid, "other_amount", e.target.value)} data-testid={`other-${r.employee_id}`} />
                          </div>}
                      </td>
                      <td className="text-right px-4 py-3 tabular font-semibold">{formatINR(liveNet(r, edits[r.employee_oid], data.locked))}</td>
                      {data.locked && <td className="px-2 py-3 text-right"><Button variant="ghost" size="icon" onClick={() => downloadOne(r)} data-testid={`download-${r.employee_id}`}><Download className="w-4 h-4" /></Button></td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!data.locked && data.rows.length > 0 && (
            <div className="flex items-center justify-between mt-6">
              <p className="text-xs text-muted-foreground">Salary basis: <b>{data.salary_basis === "net" ? "Transfer is NET" : "Transfer is GROSS"}</b> · FY {data.fy} · <span className="text-[#FF4500]">*</span> pro-rated for joiners/leavers</p>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="gap-1.5" disabled={busy} data-testid="generate-button">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Generate payslips
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Generate {monthName(month)} {year} payslips?</AlertDialogTitle>
                    <AlertDialogDescription>This creates immutable payslip records and renders PDFs for {data.rows.length} employees. The month will be locked.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={generate} data-testid="confirm-generate">Generate</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Live net for the edit view (server preview values + local deduction edits).
function liveNet(r, edit, locked) {
  if (locked) return r.net;
  const lop = Number(edit?.lop_days || 0);
  const tds = Math.round((Number(edit?.tds || 0)) * 100);
  const other = Math.round((Number(edit?.other_amount || 0)) * 100);
  const daysPaid = Math.max(0, r.window_working_days - lop);
  let gross = r.gross;
  if (r.working_days > 0 && daysPaid < r.working_days) {
    // approximate: scale the previewed full gross proportionally
    const fullGross = r.days_paid < r.working_days ? Math.round(r.gross * r.working_days / Math.max(1, r.days_paid)) : r.gross;
    gross = Math.round(fullGross * daysPaid / r.working_days);
  }
  const pt = r.pt; // slab rarely changes within edits
  return gross - pt - tds - other;
}
